import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loader2, Check, X } from 'lucide-react'
import { useAdminModelRequests, useUpdateModelRequestStatus } from '../../../hooks/use-admin-cars'

export function ModelRequestsPage() {
  const { data: requests, isLoading } = useAdminModelRequests()
  const updateStatus = useUpdateModelRequestStatus()

  const handleStatus = async (id: number, status: 'APPROVED' | 'REJECTED') => {
    await updateStatus.mutateAsync({ id, status })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Model &amp; Variant Requests</h1>
          <p className="text-sm text-muted-foreground">Review "Can't find vehicle" requests submitted by sellers and dealerships.</p>
        </div>
      </div>

      {isLoading ? (
        <div className="p-12 text-center flex flex-col items-center justify-center space-y-3">
          <Loader2 className="size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading seller model requests...</p>
        </div>
      ) : (
        <Card className="overflow-hidden border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-accent/40 text-muted-foreground border-b border-border uppercase text-[10px] tracking-wider">
              <tr>
                <th className="p-4">Requester</th>
                <th className="p-4">Brand Requested</th>
                <th className="p-4">Model &amp; Variant</th>
                <th className="p-4">Year</th>
                <th className="p-4">Notes</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(requests || []).map((req: any) => (
                <tr key={req.id} className="hover:bg-accent/20 transition-colors">
                  <td className="p-4 font-bold">
                    <div>{req.user ? `${req.user.firstName} ${req.user.lastName}` : 'User'}</div>
                    <div className="text-xs font-normal text-muted-foreground">{req.user?.email}</div>
                  </td>
                  <td className="p-4 font-bold text-primary">{req.brandName}</td>
                  <td className="p-4">
                    <div className="font-semibold">{req.modelName}</div>
                    <div className="text-xs text-muted-foreground">{req.variantName || 'Standard'}</div>
                  </td>
                  <td className="p-4 font-semibold">{req.year || 'N/A'}</td>
                  <td className="p-4 text-xs text-muted-foreground max-w-xs truncate">{req.notes || '-'}</td>
                  <td className="p-4">
                    <Badge className={
                      req.status === 'APPROVED'
                        ? 'bg-emerald-500/20 text-emerald-500'
                        : req.status === 'PENDING'
                        ? 'bg-amber-500/20 text-amber-500'
                        : 'bg-rose-500/20 text-rose-500'
                    }>
                      {req.status}
                    </Badge>
                  </td>
                  <td className="p-4 text-right">
                    {req.status === 'PENDING' && (
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white size-8 p-0" onClick={() => handleStatus(req.id, 'APPROVED')} title="Approve Request">
                          <Check className="size-4" />
                        </Button>
                        <Button size="sm" variant="destructive" className="size-8 p-0" onClick={() => handleStatus(req.id, 'REJECTED')} title="Reject Request">
                          <X className="size-4" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  )
}
